import { ArrowBack, Refresh } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { t } from "ttag";
import Tooltip from "../../components/Tooltip/LightTooltip";
import { useStoreActions } from "../../models/RootStore";
import ServiceNavigationBar from "../../widgets/Menu/ServiceNavigationBar";
import DeviceInformationCard from "../DeviceManagment/DeviceInformationCard";
import { formatSyncError } from "../DeviceSync/deviceSyncUtils";
import { auditTabPath } from "./deviceAuditsTabs";
import useStyles from "./EdgeAudits.style";
import { getDeviceTelemetryRecoveryAudit } from "./edgeAuditsApi";
import {
  formatDurationMinutes,
  formatDurationMs,
  formatTs,
  gapStatusLabel,
  gapStatusTone,
  groupGapsByStatType,
  IGapGroup,
  IGapRow,
  ITelemetryRecoveryAudit,
  outcomeLabel,
  skipBlockText,
  skipReasonLabel,
  statTypeLabel,
  statusChip,
  yesNo
} from "./edgeAuditsUtils";
import { useNgDevice } from "./useNgDevice";

const GAP_COLUMN_COUNT = 5;

const Summary = ({ label, children }: { label: string; children: React.ReactNode }) => {
  const classes = useStyles();
  return (
    <div>
      <div className={classes.summaryLabel}>{label}</div>
      <div className={classes.summaryValue}>{children}</div>
    </div>
  );
};

function totalsText(audit: ITelemetryRecoveryAudit): string {
  const totals = audit.totals || {};
  const parts = [t`${totals.gaps ?? 0} gaps`];
  (["no_response", "no_data", "partial_data", "full_data"] as const).forEach((status) => {
    if (totals[status] !== undefined) {
      parts.push(`${gapStatusLabel(status)}: ${totals[status]}`);
    }
  });
  return parts.join(" · ");
}

/** Hidden page: /devices/:deviceId/telemetry-recovery/:auditId — the gap tables of one run. */
const DeviceTelemetryRecoveryDetail = (props: any) => {
  const classes = useStyles();
  const deviceParam = props.match?.params?.deviceId || "";
  const auditId = props.match?.params?.auditId || "";
  const {
    device,
    setDevice,
    deviceId,
    loading: loadingDevice,
    error: deviceError,
    refreshDeviceStatus
  } = useNgDevice(deviceParam);
  const { addMessage } = useStoreActions((actions) => actions.errorMessage);

  const [audit, setAudit] = useState<ITelemetryRecoveryAudit | null>(null);
  const [loadingAudit, setLoadingAudit] = useState(false);
  const [auditError, setAuditError] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (!deviceId || !auditId) {
      return undefined;
    }
    let cancelled = false;
    setLoadingAudit(true);
    setAuditError("");

    getDeviceTelemetryRecoveryAudit(deviceId, auditId)
      .then((result) => {
        if (!cancelled) {
          setAudit(result || null);
        }
      })
      .catch((error) => {
        if (!cancelled) {
          const message = formatSyncError(error, t`Couldn't load this audit.`);
          setAuditError(message);
          addMessage({ message });
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingAudit(false);
        }
      });

    return () => {
      cancelled = true;
    };
    // refreshKey re-reads the audit on demand (the Refresh button)
  }, [deviceId, auditId, refreshKey, addMessage]);

  const groups: IGapGroup[] = useMemo(
    () => (audit ? groupGapsByStatType(audit.gaps, audit.statsChecked) : []),
    [audit]
  );

  // re-reads this audit and the device's connection status
  const refresh = () => {
    setRefreshKey((key) => key + 1);
    refreshDeviceStatus();
  };

  const goBackToList = () => {
    props.history.push(auditTabPath(deviceParam, "telemetry-recovery"));
  };

  const renderGapRow = (group: IGapGroup, gap: IGapRow, index: number) => (
    <TableRow hover key={`${group.statType}-${index}`} className={classes.row}>
      <TableCell className={classes.cell}>{gap.entityName || gap.entityId || "-"}</TableCell>
      <TableCell className={classes.cell}>{formatTs(gap.startTs)}</TableCell>
      <TableCell className={classes.cell}>{formatTs(gap.endTs)}</TableCell>
      <TableCell className={classes.cell}>{formatDurationMinutes(gap.durationMinutes)}</TableCell>
      <TableCell className={classes.cell}>
        <Tooltip title={gap.errorMessage || ""} placement="top">
          <Chip label={gapStatusLabel(gap.status)} size="small" color={gapStatusTone(gap.status)} variant="outlined" />
        </Tooltip>
      </TableCell>
    </TableRow>
  );

  const renderGroup = (group: IGapGroup) => {
    const emptyText = audit?.gapsTruncated
      ? t`No gap rows stored for this stat type (rows were capped; see the note above).`
      : t`No gaps found.`;
    return (
      <Box key={group.statType}>
        <Typography className={classes.sectionTitle}>
          {statTypeLabel(group.statType)} · {group.rows.length}
        </Typography>
        <Paper elevation={0} className={classes.groupPaper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell className={classes.headCell}>{t`UNIT`}</TableCell>
                <TableCell className={classes.headCell}>{t`GAP START`}</TableCell>
                <TableCell className={classes.headCell}>{t`GAP END`}</TableCell>
                <TableCell className={classes.headCell}>{t`DURATION`}</TableCell>
                <TableCell className={classes.headCell}>{t`STATUS`}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {group.rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={GAP_COLUMN_COUNT} className={classes.emptyCell}>
                    <Typography className={classes.emptyText}>{emptyText}</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                group.rows.map((gap, index) => renderGapRow(group, gap, index))
              )}
            </TableBody>
          </Table>
        </Paper>
      </Box>
    );
  };

  const renderBody = (current: ITelemetryRecoveryAudit) => {
    const chip = statusChip(current);
    const storedRows = current.gaps?.length ?? 0;
    const totalRows = current.totals?.gaps ?? storedRows;

    return (
      <Box className={classes.detailScroll}>
        <Paper elevation={0} className={classes.summaryPaper}>
          <Box className={classes.summaryGrid}>
            <Summary label={t`Started`}>{formatTs(current.startedTs)}</Summary>
            <Summary label={t`Finished`}>{formatTs(current.finishedTs)}</Summary>
            <Summary label={t`Duration`}>{formatDurationMs(current.durationMs)}</Summary>
            <Summary label={t`Outcome`}>{outcomeLabel(current)}</Summary>
            <Summary label={t`Status`}>
              <Chip label={chip.label} size="small" color={chip.tone} variant="outlined" />
            </Summary>
            {current.outcome === "skipped" && (
              <Summary label={t`Skip reason`}>{skipReasonLabel(current.skipReason)}</Summary>
            )}
            <Summary label={t`Connection`}>{formatTs(current.connectionTs)}</Summary>
            <Summary label={t`DM instance`}>{current.dmid || "-"}</Summary>
            <Summary label={t`SD card`}>{yesNo(current.sdCardConnected)}</Summary>
            <Summary label={t`Caching`}>{yesNo(current.isCachingEnabled)}</Summary>
            <Summary label={t`Stats checked`}>
              {current.statsChecked?.length ? current.statsChecked.join(", ") : "-"}
            </Summary>
            <Summary label={t`Totals`}>{totalsText(current)}</Summary>
          </Box>
        </Paper>

        {!current.statsChecked?.length ? (
          <Typography className={classes.infoBlock}>{skipBlockText(current)}</Typography>
        ) : (
          <>
            {current.outcome === "abortedDisconnected" && (
              <Alert severity="info">
                {t`The device disconnected during this run; only the stat types listed above were reached.`}
              </Alert>
            )}
            {current.gapsTruncated && (
              <Alert severity="warning">
                {t`Showing the ${storedRows} stored gap rows of ${totalRows}. Totals and flags reflect every gap found; rows were kept by status priority, so a stat type may show none.`}
              </Alert>
            )}
            {groups.map(renderGroup)}
          </>
        )}
      </Box>
    );
  };

  return (
    <ServiceNavigationBar title={t`NG telemetry recovery audit`} {...props}>
      <Box className={classes.page}>
        {loadingDevice && (
          <Box display="flex" justifyContent="center" mt={6}>
            <CircularProgress />
          </Box>
        )}

        {!loadingDevice && deviceError && <Typography className={classes.empty}>{deviceError}</Typography>}

        {!loadingDevice && !deviceError && device && (
          <>
            <Box className={classes.toolbar}>
              <span className={classes.toolbarSpacer} />
              <Button variant="outlined" className={classes.actionButton} startIcon={<Refresh />} onClick={refresh}>
                {t`Refresh`}
              </Button>
            </Box>

            <DeviceInformationCard
              device={device}
              setDevice={setDevice}
              deviceId={deviceId}
              history={props.history}
              companion="terminal"
              layout="row"
              compact
              onRetryConnection={refreshDeviceStatus}
            />

            <Box className={classes.toolbar}>
              <Button size="small" startIcon={<ArrowBack />} onClick={goBackToList}>
                {t`Back to audits`}
              </Button>
              <span className={classes.toolbarSpacer} />
              <Typography className={classes.mono}>{auditId}</Typography>
            </Box>

            {loadingAudit && !audit && (
              <Box display="flex" justifyContent="center" mt={4}>
                <CircularProgress />
              </Box>
            )}
            {!loadingAudit && auditError && <Typography className={classes.empty}>{auditError}</Typography>}
            {!auditError && audit && renderBody(audit)}
          </>
        )}
      </Box>
    </ServiceNavigationBar>
  );
};

export default DeviceTelemetryRecoveryDetail;
